import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Btn from './ui/Btn';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';
import { Partie, PartieRole, PdfMetadata, TimelineEvent } from '../lib/types';

/**
 * Onglet « Timeline » du hub dossier (M3 / Sprint 7-1).
 * Frise chronologique : événements groupés par mois, ajout / édition /
 * suppression manuels. L'extraction IA des dates depuis le texte OCR
 * arrivera Sprint 7-2 et alimentera les mêmes fiches (verified=false).
 */

const EVENT_TYPE_SUGGESTIONS = [
  'audition',
  'perquisition',
  'garde à vue',
  'mandat',
  'détention',
  'libération',
  'audience',
  'jugement',
  'expertise',
  'PV constat',
  'plainte',
  'décès',
  'fait',
  'autre',
];

const MONTH_LABELS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

type DraftForm = {
  event_date: string;
  event_time: string;
  event_type: string;
  description: string;
  source_quote: string;
  pdf_file_id: string;
};

const EMPTY_DRAFT: DraftForm = {
  event_date: '',
  event_time: '',
  event_type: '',
  description: '',
  source_quote: '',
  pdf_file_id: '',
};

const ROLE_LABELS_SHORT: Record<PartieRole, string> = {
  prevenu: 'prévenu',
  partie_civile: 'partie civile',
  victime: 'victime',
  temoin: 'témoin',
  enqueteur: 'enquêteur',
  expert: 'expert',
  autre: 'autre',
};

type Props = {
  dossierId: string;
  pdfs: PdfMetadata[];
  onCountChange?: (count: number) => void;
  onEventsChange?: (events: TimelineEvent[]) => void;
};

type FilterPartyMode = 'all' | string;

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${parseInt(d!, 10)} ${MONTH_LABELS[parseInt(m!, 10) - 1]} ${y}`;
}

function formatTime(t: string | null): string | null {
  if (!t) return null;
  return t.slice(0, 5); // HH:MM
}

function monthKey(iso: string): string {
  return iso.slice(0, 7); // YYYY-MM
}

function monthHeader(key: string): string {
  const [y, m] = key.split('-');
  return `${MONTH_LABELS[parseInt(m!, 10) - 1]} ${y}`;
}

function pieceLabel(p: PdfMetadata | undefined): string {
  if (!p) return '';
  return p.titre_pv || p.filename_original;
}

export default function TimelineTab({ dossierId, pdfs, onCountChange, onEventsChange }: Props) {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [parties, setParties] = useState<Partie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<DraftForm>(EMPTY_DRAFT);
  const [submitting, setSubmitting] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<DraftForm>(EMPTY_DRAFT);

  const [filterParty, setFilterParty] = useState<FilterPartyMode>('all');

  const pdfById = useMemo(() => {
    const m = new Map<string, PdfMetadata>();
    for (const p of pdfs) m.set(p.id, p);
    return m;
  }, [pdfs]);

  const load = async () => {
    setLoading(true);
    try {
      const [rows, pts] = await Promise.all([
        api.get<TimelineEvent[]>(`/dossiers/${dossierId}/timeline`),
        api.get<Partie[]>(`/dossiers/${dossierId}/parties`),
      ]);
      setEvents(rows);
      setParties(pts);
      onCountChange?.(rows.length);
      onEventsChange?.(rows);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setLoading(false);
    }
  };

  const partyById = useMemo(() => {
    const m = new Map<string, Partie>();
    for (const p of parties) m.set(p.id, p);
    return m;
  }, [parties]);

  const updatePartyLinks = async (eventId: string, partyIds: string[]) => {
    await api.patch<TimelineEvent>(`/timeline/${eventId}`, { partie_ids: partyIds });
    await load();
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId]);

  const filteredEvents = useMemo(
    () =>
      events.filter((e) => {
        if (filterParty === 'all') return true;
        if (filterParty === '__none__') return e.linked_party_ids.length === 0;
        return e.linked_party_ids.includes(filterParty);
      }),
    [events, filterParty]
  );

  const grouped = useMemo(() => {
    const m = new Map<string, TimelineEvent[]>();
    for (const e of filteredEvents) {
      const k = monthKey(e.event_date);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(e);
    }
    return m;
  }, [filteredEvents]);

  const monthKeys = useMemo(() => Array.from(grouped.keys()).sort(), [grouped]);

  // ---- Actions --------------------------------------------------------

  const buildPayload = (d: DraftForm): Record<string, unknown> => {
    const out: Record<string, unknown> = {
      event_date: d.event_date,
      description: d.description.trim(),
    };
    if (d.event_time)
      out.event_time = d.event_time.length === 5 ? `${d.event_time}:00` : d.event_time;
    if (d.event_type.trim()) out.event_type = d.event_type.trim();
    if (d.source_quote.trim()) out.source_quote = d.source_quote.trim();
    if (d.pdf_file_id) out.pdf_file_id = d.pdf_file_id;
    return out;
  };

  const submitNew = async () => {
    if (!draft.event_date || !draft.description.trim()) return;
    setSubmitting(true);
    try {
      await api.post<TimelineEvent>(`/dossiers/${dossierId}/timeline`, buildPayload(draft));
      setAdding(false);
      setDraft(EMPTY_DRAFT);
      await load();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (e: TimelineEvent) => {
    setEditingId(e.id);
    setEditDraft({
      event_date: e.event_date,
      event_time: e.event_time?.slice(0, 5) ?? '',
      event_type: e.event_type ?? '',
      description: e.description,
      source_quote: e.source_quote ?? '',
      pdf_file_id: e.pdf_file_id ?? '',
    });
  };

  const submitEdit = async () => {
    if (!editingId || !editDraft.event_date || !editDraft.description.trim()) return;
    setSubmitting(true);
    try {
      await api.patch<TimelineEvent>(`/timeline/${editingId}`, {
        event_date: editDraft.event_date,
        event_time: editDraft.event_time
          ? editDraft.event_time.length === 5
            ? `${editDraft.event_time}:00`
            : editDraft.event_time
          : null,
        event_type: editDraft.event_type.trim() || null,
        description: editDraft.description.trim(),
        source_quote: editDraft.source_quote.trim() || null,
        pdf_file_id: editDraft.pdf_file_id || null,
      });
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (e: TimelineEvent) => {
    if (!window.confirm(`Supprimer l'événement « ${e.description.slice(0, 60)}… » ?`)) return;
    try {
      await api.delete(`/timeline/${e.id}`);
      await load();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  // ADR-068 GC : dates IA non vérifiées identifiées par le préfixe
  // description (ADR-065) ou le legacy event_type 'ai_dates_v1'.
  const aiUnverified = events.filter(
    (e) =>
      !e.verified &&
      (e.description.startsWith('Date détectée par IA') || e.event_type === 'ai_dates_v1')
  );

  const purgeAiUnverified = async () => {
    if (aiUnverified.length === 0) return;
    if (
      !window.confirm(
        `Supprimer ${aiUnverified.length} évènement${aiUnverified.length > 1 ? 's' : ''} IA non vérifié${aiUnverified.length > 1 ? 's' : ''} ?\n\n` +
          `Les évènements bascules à « ✓ vérifié » sont conservés.`
      )
    )
      return;
    try {
      await Promise.all(aiUnverified.map((e) => api.delete(`/timeline/${e.id}`)));
      toast.success(`${aiUnverified.length} évènement(s) IA supprimé(s).`);
      await load();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  // ---- Render ---------------------------------------------------------

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-rule bg-paper-elevated p-3">
        <div className="flex items-center gap-3">
          <span className="text-[12px] text-text-secondary">
            <strong className="font-medium text-text-primary">{filteredEvents.length}</strong>
            {filteredEvents.length !== events.length && (
              <span className="text-text-tertiary">/{events.length}</span>
            )}{' '}
            évènement{filteredEvents.length > 1 ? 's' : ''}
          </span>
          <select
            value={filterParty}
            onChange={(e) => setFilterParty(e.target.value)}
            className="rounded-md border border-rule-strong bg-paper-elevated px-2 py-1 text-xs"
            disabled={parties.length === 0}
          >
            <option value="all">Toutes parties ({parties.length})</option>
            {parties.length > 0 && <option value="__none__">— sans partie —</option>}
            {parties.map((p) => (
              <option key={p.id} value={p.id}>
                {[p.prenom, p.nom].filter(Boolean).join(' ')} · {ROLE_LABELS_SHORT[p.role]}
                {p.is_client ? ' (client)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          {aiUnverified.length > 0 && (
            <Btn
              variant="ghost"
              size="sm"
              onClick={() => void purgeAiUnverified()}
              title="Supprimer en lot tous les évènements IA non vérifiés"
            >
              ✕ {aiUnverified.length} IA
            </Btn>
          )}
          {!adding && (
            <Btn
              variant="primary"
              size="sm"
              onClick={() => {
                setDraft(EMPTY_DRAFT);
                setAdding(true);
              }}
            >
              + Ajouter un évènement
            </Btn>
          )}
        </div>
      </div>

      {adding && (
        <EventForm
          draft={draft}
          setDraft={setDraft}
          pdfs={pdfs}
          onCancel={() => {
            setAdding(false);
            setDraft(EMPTY_DRAFT);
          }}
          onSubmit={() => void submitNew()}
          submitting={submitting}
          submitLabel="Créer l'évènement"
        />
      )}

      {error && (
        <p className="rounded-md border border-cat-def/40 bg-cat-def-pastel px-3 py-2 text-sm text-cat-def">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-text-secondary">Chargement…</p>
      ) : events.length === 0 && !adding ? (
        <p className="rounded-md border border-dashed border-rule-strong bg-paper-card p-10 text-center text-sm text-text-secondary">
          Aucun évènement enregistré. Ajoute les jalons clés du dossier (faits, audition,
          perquisition, audience…).
        </p>
      ) : (
        <div className="relative space-y-6">
          {monthKeys.map((mk) => {
            const rows = grouped.get(mk)!;
            return (
              <section key={mk} className="space-y-2">
                <header className="sticky top-0 z-10 -mx-1 bg-paper py-1.5">
                  <h3 className="font-serif text-[15px] font-medium text-text-primary">
                    {monthHeader(mk)} <span className="text-text-tertiary">· {rows.length}</span>
                  </h3>
                </header>
                <ul className="space-y-2 border-l-2 border-rule pl-4">
                  {rows.map((e) =>
                    editingId === e.id ? (
                      <li key={e.id}>
                        <EventForm
                          draft={editDraft}
                          setDraft={setEditDraft}
                          pdfs={pdfs}
                          onCancel={() => setEditingId(null)}
                          onSubmit={() => void submitEdit()}
                          submitting={submitting}
                          submitLabel="Enregistrer"
                        />
                      </li>
                    ) : (
                      <li key={e.id} className="relative">
                        <span
                          className={`absolute -left-[21px] top-3 inline-flex h-3 w-3 rounded-full border-2 ${
                            e.is_future && !e.is_checked
                              ? 'border-rule-strong bg-paper-subtle'
                              : e.verified
                                ? 'border-accent bg-paper-elevated'
                                : 'border-rule-strong bg-paper-subtle'
                          }`}
                          aria-hidden
                        />
                        <div
                          className={
                            e.is_future && !e.is_checked
                              ? 'opacity-50 transition hover:opacity-100'
                              : ''
                          }
                        >
                          <EventCard
                            event={e}
                            pdf={e.pdf_file_id ? pdfById.get(e.pdf_file_id) : undefined}
                            dossierId={dossierId}
                            parties={parties}
                            partyById={partyById}
                            onEdit={() => startEdit(e)}
                            onDelete={() => void remove(e)}
                            onPartyLinksChange={(ids) => updatePartyLinks(e.id, ids)}
                            onToggleChecked={async () => {
                              try {
                                await api.patch(`/timeline/${e.id}`, {
                                  is_checked: !e.is_checked,
                                });
                                await load();
                              } catch (err) {
                                toast.error(
                                  err instanceof ApiError ? err.message : 'Erreur réseau'
                                );
                              }
                            }}
                          />
                        </div>
                      </li>
                    )
                  )}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function EventCard({
  event: e,
  pdf,
  dossierId,
  parties,
  partyById,
  onEdit,
  onDelete,
  onPartyLinksChange,
  onToggleChecked,
}: {
  event: TimelineEvent;
  pdf: PdfMetadata | undefined;
  dossierId: string;
  parties: Partie[];
  partyById: Map<string, Partie>;
  onEdit: () => void;
  onDelete: () => void;
  onPartyLinksChange: (partyIds: string[]) => Promise<void>;
  onToggleChecked?: () => Promise<void>;
}) {
  const time = formatTime(e.event_time);
  const [partyMenuOpen, setPartyMenuOpen] = useState(false);
  const linkedParties = e.linked_party_ids
    .map((id) => partyById.get(id))
    .filter((p): p is Partie => Boolean(p));
  const unlinkedParties = parties.filter((p) => !e.linked_party_ids.includes(p.id));
  const togglePartyLink = async (partyId: string) => {
    const next = e.linked_party_ids.includes(partyId)
      ? e.linked_party_ids.filter((x) => x !== partyId)
      : [...e.linked_party_ids, partyId];
    await onPartyLinksChange(next);
  };
  return (
    <div className="group rounded-sm border border-rule bg-paper-elevated p-3.5 hover:bg-paper-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-mono text-[12px] font-medium text-text-primary">
              {formatDate(e.event_date)}
              {time && <span className="ml-1.5 text-text-secondary">· {time}</span>}
            </span>
            {e.event_type && (
              <span className="inline-flex items-center rounded-pill bg-accent-light px-2 py-[2px] text-[10px] font-medium uppercase tracking-[0.3px] text-accent-text">
                {e.event_type}
              </span>
            )}
            {!e.verified && !e.is_future && (
              <span
                className="inline-flex items-center rounded-pill bg-cat-imp-pastel px-2 py-[2px] text-[10px] font-medium uppercase tracking-[0.3px] text-cat-imp-text"
                title="Date extraite automatiquement, à confirmer"
              >
                à vérifier
              </span>
            )}
            {e.is_future && !e.is_checked && (
              <span
                className="inline-flex items-center rounded-pill bg-paper-subtle px-2 py-[2px] text-[10px] font-medium uppercase tracking-[0.3px] text-text-tertiary"
                title="Étape à venir — coche-la quand on y est arrivé"
              >
                à venir
              </span>
            )}
            {e.is_future && e.is_checked && (
              <span
                className="inline-flex items-center rounded-pill bg-cat-fav-pastel px-2 py-[2px] text-[10px] font-medium uppercase tracking-[0.3px] text-cat-fav-darker"
                title="Étape future confirmée par l'avocat"
              >
                ✓ arrivé
              </span>
            )}
            {e.is_future && onToggleChecked && (
              <button
                type="button"
                onClick={() => void onToggleChecked()}
                className="inline-flex items-center gap-1 rounded-md border border-rule px-2 py-[2px] text-[10.5px] text-text-secondary hover:border-accent hover:text-accent-text"
                title={e.is_checked ? 'Re-marquer comme à venir' : 'On y est : passer en plein'}
              >
                {e.is_checked ? '↶ Re-griser' : '✓ On y est'}
              </button>
            )}
          </div>
          <p className="mt-1.5 whitespace-pre-wrap text-[13px] leading-relaxed text-text-primary">
            {e.description}
          </p>
          {e.source_quote && (
            <p className="mt-1 font-serif text-[12px] italic text-text-secondary">
              « {e.source_quote} »
            </p>
          )}
          {pdf && (
            <Link
              to={`/dossiers/${dossierId}/pieces/${pdf.id}`}
              className="mt-1 inline-block font-mono text-[11px] text-text-link hover:underline"
            >
              source : {pieceLabel(pdf)} →
            </Link>
          )}
          {/* Party chips — ADR-043 */}
          {(linkedParties.length > 0 || parties.length > 0) && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {linkedParties.map((p) => (
                <button
                  key={p.id}
                  onClick={() => void togglePartyLink(p.id)}
                  className={`group/chip inline-flex items-center gap-1 rounded-pill px-2 py-[2px] text-[10px] font-medium ${
                    p.is_client
                      ? 'bg-accent-light text-accent-text'
                      : 'bg-paper-subtle text-text-secondary'
                  } hover:bg-cat-def-pastel hover:text-cat-def`}
                  title={`${ROLE_LABELS_SHORT[p.role]} — cliquer pour délier`}
                >
                  <span>
                    {p.prenom ? `${p.prenom[0]!}. ` : ''}
                    {p.nom}
                  </span>
                  <span className="opacity-0 transition group-hover/chip:opacity-100">✕</span>
                </button>
              ))}
              {unlinkedParties.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setPartyMenuOpen((v) => !v)}
                    className="rounded-pill border border-dashed border-rule-strong bg-paper-elevated px-2 py-[2px] text-[10px] font-medium text-text-tertiary hover:border-accent hover:text-accent-text"
                    title="Lier une partie à cet évènement"
                  >
                    + partie
                  </button>
                  {partyMenuOpen && (
                    <ul className="absolute left-0 top-full z-20 mt-1 max-h-56 w-56 overflow-y-auto rounded-md border border-rule bg-paper-elevated shadow-md">
                      {unlinkedParties.map((p) => (
                        <li key={p.id}>
                          <button
                            onClick={async () => {
                              setPartyMenuOpen(false);
                              await togglePartyLink(p.id);
                            }}
                            className="block w-full px-2.5 py-1.5 text-left text-[12px] hover:bg-paper-subtle"
                          >
                            <span className="font-medium text-text-primary">
                              {p.prenom ? `${p.prenom} ` : ''}
                              {p.nom}
                            </span>
                            <span className="ml-1.5 text-[10px] uppercase tracking-[0.3px] text-text-tertiary">
                              {ROLE_LABELS_SHORT[p.role]}
                            </span>
                            {p.is_client && (
                              <span className="ml-1.5 text-[10px] font-semibold text-accent-text">
                                client
                              </span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1 opacity-0 transition group-hover:opacity-100">
          <button
            onClick={onEdit}
            className="rounded p-1 text-text-tertiary hover:bg-paper-subtle hover:text-text-primary"
            title="Modifier"
          >
            ✎
          </button>
          <button
            onClick={onDelete}
            className="rounded p-1 text-text-tertiary hover:bg-cat-def-pastel hover:text-cat-def"
            title="Supprimer"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

function EventForm({
  draft,
  setDraft,
  pdfs,
  onCancel,
  onSubmit,
  submitting,
  submitLabel,
}: {
  draft: DraftForm;
  setDraft: (d: DraftForm) => void;
  pdfs: PdfMetadata[];
  onCancel: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitLabel: string;
}) {
  const labelCls = 'block text-xs font-medium uppercase tracking-[0.3px] text-text-secondary';
  const inputCls =
    'mt-1 w-full rounded-sm border border-rule-strong bg-paper-elevated px-2.5 py-1.5 text-sm font-normal normal-case tracking-normal text-text-primary';
  return (
    <div className="space-y-3 rounded-md border border-accent/40 bg-accent-lighter p-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className={labelCls}>
          Date *
          <input
            type="date"
            value={draft.event_date}
            onChange={(e) => setDraft({ ...draft, event_date: e.target.value })}
            required
            autoFocus
            className={inputCls}
          />
        </label>
        <label className={labelCls}>
          Heure
          <input
            type="time"
            value={draft.event_time}
            onChange={(e) => setDraft({ ...draft, event_time: e.target.value })}
            className={inputCls}
          />
        </label>
        <label className={labelCls}>
          Type
          <input
            value={draft.event_type}
            onChange={(e) => setDraft({ ...draft, event_type: e.target.value })}
            list="event-type-suggestions"
            placeholder="audition, perquisition…"
            className={inputCls}
          />
          <datalist id="event-type-suggestions">
            {EVENT_TYPE_SUGGESTIONS.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </label>
      </div>
      <label className={labelCls}>
        Description *
        <textarea
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          rows={2}
          required
          className={inputCls}
        />
      </label>
      <label className={labelCls}>
        Citation source
        <textarea
          value={draft.source_quote}
          onChange={(e) => setDraft({ ...draft, source_quote: e.target.value })}
          rows={2}
          placeholder="Extrait littéral de la pièce qui établit la date / l'évènement"
          className={inputCls}
        />
      </label>
      <label className={labelCls}>
        Pièce source
        <select
          value={draft.pdf_file_id}
          onChange={(e) => setDraft({ ...draft, pdf_file_id: e.target.value })}
          className={inputCls}
        >
          <option value="">— aucune —</option>
          {pdfs.map((p) => (
            <option key={p.id} value={p.id}>
              {pieceLabel(p)}
            </option>
          ))}
        </select>
      </label>
      <div className="flex justify-end gap-1.5">
        <Btn variant="ghost" size="sm" onClick={onCancel} disabled={submitting}>
          Annuler
        </Btn>
        <Btn
          variant="primary"
          size="sm"
          onClick={onSubmit}
          disabled={submitting || !draft.event_date || !draft.description.trim()}
        >
          {submitting ? '…' : submitLabel}
        </Btn>
      </div>
    </div>
  );
}
